import type { TFunction } from 'i18next';

import type { WorkOrderModuleCategoriesSettings } from 'lane-shared/domains/workOrder';

import { parseDurationToSeconds } from './targetTimesFormat';

const V = {
  category: 'web.admin.serviceRequest.settings.targetTimes.form.validation.category',
  duration:
    'web.admin.serviceRequest.settings.targetTimes.form.validation.duration',
  positiveDuration:
    'web.admin.serviceRequest.settings.targetTimes.form.validation.positiveDuration',
  noIssuesInCategory:
    'web.admin.serviceRequest.settings.targetTimes.form.validation.noIssuesInCategory',
  selectionStale:
    'web.admin.serviceRequest.settings.targetTimes.form.validation.selectionStale',
} as const;

export type ParsedSubmit =
  | { ok: false; error: string }
  | {
      ok: true;
      category: string;
      targetResponseSeconds: number;
      targetCompletionSeconds: number;
      issues: string[];
    };

type IssueResolve =
  | { ok: true; issues: string[] }
  | { ok: false; errorKey: string };

type IssueResolutionInput = {
  issueValue: string;
  allIssuesSentinel: string;
  failedRetryBatchSentinel?: string;
  pendingRetryIssues?: string[] | null;
  categoryIssueStrings: string[];
  creatableIssues: string[];
  existingRuleSet: Set<string>;
};

function tryResolvePendingSelections(
  input: IssueResolutionInput,
  pending: string[]
): IssueResolve | undefined {
  const batchSentinel = input.failedRetryBatchSentinel;
  const pendingSet = new Set(pending);
  const { existingRuleSet } = input;

  if (batchSentinel && input.issueValue === batchSentinel) {
    return {
      ok: true,
      issues: pending.filter(issue => !existingRuleSet.has(issue)),
    };
  }

  if (pendingSet.has(input.issueValue)) {
    return existingRuleSet.has(input.issueValue)
      ? { ok: true, issues: [] }
      : { ok: true, issues: [input.issueValue] };
  }

  if (input.issueValue === input.allIssuesSentinel) {
    return {
      ok: true,
      issues: pending.filter(issue => !existingRuleSet.has(issue)),
    };
  }

  return undefined;
}

function resolveWithoutPendingIssueSelection(
  input: IssueResolutionInput
): IssueResolve {
  if (input.issueValue === input.allIssuesSentinel) {
    return { ok: true, issues: input.creatableIssues };
  }

  if (
    input.existingRuleSet.has(input.issueValue) ||
    !input.categoryIssueStrings.includes(input.issueValue)
  ) {
    return { ok: false, errorKey: V.selectionStale };
  }

  return { ok: true, issues: [input.issueValue] };
}

function resolveTargetTimeModalIssues(
  input: IssueResolutionInput
): IssueResolve {
  const pending = input.pendingRetryIssues?.filter(Boolean) ?? [];

  if (pending.length > 0) {
    const pendingResult = tryResolvePendingSelections(input, pending);

    if (pendingResult !== undefined) {
      return pendingResult;
    }
  }

  return resolveWithoutPendingIssueSelection(input);
}

function emptyIssuesErrorKey(input: {
  categoryIssueStrings: string[];
  creatableIssues: string[];
  pending: string[];
}): string {
  if (input.categoryIssueStrings.length === 0) {
    return V.noIssuesInCategory;
  }

  if (input.creatableIssues.length === 0 || input.pending.length > 0) {
    return V.selectionStale;
  }

  return V.noIssuesInCategory;
}

type CategoryAndDurationsResult =
  | { ok: false; errorKey: string }
  | {
      ok: true;
      category: string;
      targetResponseSeconds: number;
      targetCompletionSeconds: number;
    };

export type TargetTimeDurationFieldsInput = {
  respHours: string;
  respMinutes: string;
  compHours: string;
  compMinutes: string;
};

export type ParsedTargetTimeDurations =
  | { ok: false; errorKey: string }
  | {
      ok: true;
      targetResponseSeconds: number;
      targetCompletionSeconds: number;
    };

export function parseTargetTimeDurationFields(
  fields: TargetTimeDurationFieldsInput
): ParsedTargetTimeDurations {
  const targetResponseSeconds = parseDurationToSeconds(
    fields.respHours,
    fields.respMinutes
  );
  const targetCompletionSeconds = parseDurationToSeconds(
    fields.compHours,
    fields.compMinutes
  );

  if (targetResponseSeconds === null || targetCompletionSeconds === null) {
    return { ok: false, errorKey: V.duration };
  }

  if (targetResponseSeconds <= 0 || targetCompletionSeconds <= 0) {
    return { ok: false, errorKey: V.positiveDuration };
  }

  return { ok: true, targetResponseSeconds, targetCompletionSeconds };
}

function parseCategoryAndDurations(input: {
  categoryValue: string | null;
  respHours: string;
  respMinutes: string;
  compHours: string;
  compMinutes: string;
}): CategoryAndDurationsResult {
  const selectedCategoryValue = input.categoryValue;

  if (!selectedCategoryValue?.trim()) {
    return { ok: false, errorKey: V.category };
  }

  const category = selectedCategoryValue;
  const durations = parseTargetTimeDurationFields(input);

  if (!durations.ok) {
    return durations;
  }

  return {
    ok: true,
    category,
    targetResponseSeconds: durations.targetResponseSeconds,
    targetCompletionSeconds: durations.targetCompletionSeconds,
  };
}

export function parseTargetTimeModalSubmit(input: {
  categoryValue: string | null;
  issueValue: string;
  respHours: string;
  respMinutes: string;
  compHours: string;
  compMinutes: string;
  categories: WorkOrderModuleCategoriesSettings[];
  allIssuesSentinel: string;
  /** Issue strings in this category that already have a target-time rule for this channel. */
  issuesWithExistingRules?: string[];
  /** When set, selecting this value retries every issue still listed in `pendingRetryIssues`. */
  failedRetryBatchSentinel?: string;
  /** After a partial multi-issue failure, the issues that still need to be created. */
  pendingRetryIssues?: string[] | null;
  t: TFunction;
}): ParsedSubmit {
  const base = parseCategoryAndDurations({
    categoryValue: input.categoryValue,
    respHours: input.respHours,
    respMinutes: input.respMinutes,
    compHours: input.compHours,
    compMinutes: input.compMinutes,
  });

  if (!base.ok) {
    return { ok: false, error: input.t(base.errorKey) };
  }

  const matchingCategory = input.categories.find(
    categorySettings => categorySettings.firendlyName === base.category
  );
  const categoryIssueStrings = (matchingCategory?.issues ?? []).filter(
    issue => (issue ?? '').trim() !== ''
  );
  const existingRuleSet = new Set(input.issuesWithExistingRules ?? []);
  const creatableIssues = categoryIssueStrings.filter(
    issue => !existingRuleSet.has(issue)
  );
  const pending = input.pendingRetryIssues?.filter(Boolean) ?? [];

  const issueResolution = resolveTargetTimeModalIssues({
    issueValue: input.issueValue,
    allIssuesSentinel: input.allIssuesSentinel,
    failedRetryBatchSentinel: input.failedRetryBatchSentinel,
    pendingRetryIssues: input.pendingRetryIssues,
    categoryIssueStrings,
    creatableIssues,
    existingRuleSet,
  });

  if (!issueResolution.ok) {
    return {
      ok: false,
      error: input.t(issueResolution.errorKey),
    };
  }

  const { issues } = issueResolution;

  if (issues.length === 0) {
    return {
      ok: false,
      error: input.t(
        emptyIssuesErrorKey({
          categoryIssueStrings,
          creatableIssues,
          pending,
        })
      ),
    };
  }

  return {
    ok: true,
    category: base.category,
    targetResponseSeconds: base.targetResponseSeconds,
    targetCompletionSeconds: base.targetCompletionSeconds,
    issues,
  };
}
